import type * as React from "react";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";

import { useToolPanelVariant } from "./tool-panel-variant-context";

export interface ActionButton {
	/** Button label text */
	label: string;
	/** Click handler */
	onClick: () => void;
	/** Disabled state */
	disabled?: boolean;
	/** Loading state (shows spinner) */
	loading?: boolean;
	/** Button variant */
	variant?: "contained" | "outlined" | "text";
	/** MUI Button color */
	color?:
		| "primary"
		| "secondary"
		| "error"
		| "info"
		| "warning"
		| "success"
		| "inherit";
}

export interface ToolPanelActionsProps {
	/** Predefined action patterns for common use cases */
	variant: "clear-submit" | "cancel-save" | "reset-apply" | "custom";
	/** Primary action button (right side, emphasized) - optional when variant is "custom" */
	primaryAction?: ActionButton;
	/** Secondary action button (left side, de-emphasized) */
	secondaryAction?: ActionButton;
	/** Custom action layout - overrides predefined patterns */
	children?: React.ReactNode;
	/** Layout direction */
	direction?: "space-between" | "flex-end";
}

const VARIANT_DEFAULTS = {
	"clear-submit": {
		secondary: { label: "Clear", variant: "outlined" as const },
		primary: { label: "Submit", variant: "contained" as const },
	},
	"cancel-save": {
		secondary: { label: "Cancel", variant: "outlined" as const },
		primary: { label: "Save", variant: "contained" as const },
	},
	"reset-apply": {
		secondary: { label: "Reset", variant: "outlined" as const },
		primary: { label: "Apply", variant: "contained" as const },
	},
	custom: {},
} as const;

export function ToolPanelActions({
	variant,
	primaryAction,
	secondaryAction,
	children,
	direction = "space-between",
}: ToolPanelActionsProps): React.JSX.Element {
	// Mobile drawer footers (drawer-content variant) standardise the secondary
	// action to a left-aligned text button in secondary color. Desktop panels
	// keep the preset variant defaults (outlined).
	const panelVariant = useToolPanelVariant();
	const isDrawerContent = panelVariant === "drawer-content";

	// Use custom children if provided
	if (variant === "custom" && children) {
		return <>{children}</>;
	}

	// Require primaryAction for non-custom variants
	if (!primaryAction) {
		throw new Error(
			"primaryAction is required when variant is not 'custom' with children",
		);
	}

	const defaults = VARIANT_DEFAULTS[variant];

	// Apply default labels and variants if not specified
	const resolvedPrimary: ActionButton = {
		...(defaults && "primary" in defaults ? defaults.primary : {}),
		...primaryAction,
		variant: primaryAction?.variant ?? "contained",
	};

	const presetSecondary: Partial<ActionButton> =
		defaults && "secondary" in defaults ? defaults.secondary : {};
	const mobileSecondaryOverrides: Partial<ActionButton> = isDrawerContent
		? { variant: "text", color: "secondary" }
		: {};

	const resolvedSecondary: ActionButton | undefined = secondaryAction
		? {
				...presetSecondary,
				...mobileSecondaryOverrides,
				...secondaryAction,
				variant:
					secondaryAction.variant ??
					mobileSecondaryOverrides.variant ??
					presetSecondary.variant ??
					"outlined",
				color:
					secondaryAction.color ?? mobileSecondaryOverrides.color ?? undefined,
			}
		: undefined;

	const justifyContent =
		direction === "space-between" ? "space-between" : "flex-end";

	return (
		<Stack
			direction="row"
			spacing={2}
			sx={{
				justifyContent,
				...(isDrawerContent ? { width: "100%" } : {}),
			}}>
			{resolvedSecondary && (
				<Button
					variant={resolvedSecondary.variant}
					color={resolvedSecondary.color}
					onClick={resolvedSecondary.onClick}
					disabled={resolvedSecondary.disabled}
					size="small">
					{resolvedSecondary.label}
				</Button>
			)}

			<Button
				variant={resolvedPrimary.variant}
				color={resolvedPrimary.color}
				onClick={resolvedPrimary.onClick}
				disabled={resolvedPrimary.disabled ?? resolvedPrimary.loading}
				size="small"
				startIcon={
					resolvedPrimary.loading ? (
						<CircularProgress
							size={16}
							color="inherit"
						/>
					) : undefined
				}>
				{resolvedPrimary.label}
			</Button>
		</Stack>
	);
}
